import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { getNewsById, getMovieById, getRelatedNews } from "@/utils/content";

export default function NewsDetail() {
  const { id } = useParams();
  const article = getNewsById(id);

  if (!article) {
    return (
      <div className="w-full px-4 md:px-8 py-16 flex flex-col items-center">
        <i className="ri-emotion-sad-line text-5xl text-background-400"></i>
        <h1 className="mt-4 text-xl font-heading font-semibold text-foreground-950">
          新闻内容不存在
        </h1>
        <p className="mt-2 text-sm text-foreground-600">该资讯可能已被删除或链接有误。</p>
        <Link
          to="/news"
          className="mt-6 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-label font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
        >
          返回资讯列表
        </Link>
      </div>
    );
  }

  const related = getRelatedNews(article, 3);
  const relatedMovies = article.relatedMovieIds
    .map((mid) => getMovieById(mid))
    .filter((m): m is NonNullable<typeof m> => Boolean(m));

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: "影视资讯", to: "/news" }, { label: article.category }]} />

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <article className="lg:col-span-2">
          <div className="rounded-lg bg-background-50 border border-background-200 overflow-hidden">
            <div className="w-full aspect-[16/9] bg-background-200">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 欲望之岛资讯`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-6 md:p-8">
              <span className="inline-block px-2 py-0.5 rounded bg-accent-100 text-accent-700 text-xs font-label">
                {article.category}
              </span>
              <h1 className="mt-3 text-xl md:text-3xl font-heading font-bold text-foreground-950 leading-snug">
                {article.title}
              </h1>
              <div className="mt-3 flex items-center gap-4 text-xs md:text-sm text-foreground-500 font-label">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
              </div>

              <div className="mt-6 pt-6 border-t border-background-200">
                <p className="text-sm md:text-base text-foreground-700 leading-loose font-medium">
                  {article.summary}
                </p>
                {article.content.map((para, i) => (
                  <p
                    key={i}
                    className="mt-4 text-sm md:text-base text-foreground-700 leading-loose"
                  >
                    {para}
                  </p>
                ))}
              </div>

              {relatedMovies.length > 0 && (
                <div className="mt-8 p-5 rounded-lg bg-background-100 border border-background-200">
                  <h2 className="text-sm font-heading font-semibold text-foreground-950">
                    相关影视作品
                  </h2>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {relatedMovies.map((m) => (
                      <Link
                        key={m.id}
                        to={`/detail/${m.id}`}
                        className="px-3 py-1.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-800 hover:border-accent-400 hover:text-accent-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-film-line mr-1"></i>
                        {m.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </article>

        <aside>
          <div className="rounded-lg bg-background-50 border border-background-200 p-5">
            <h2 className="text-lg font-heading font-bold text-foreground-950 flex items-center gap-2">
              <i className="ri-links-line text-accent-500"></i>
              相关文章
            </h2>
            <ul className="mt-4 flex flex-col gap-4">
              {related.map((n) => (
                <li key={n.id}>
                  <Link to={`/news/${n.id}`} className="group block cursor-pointer">
                    <div className="w-full aspect-[16/9] rounded-md overflow-hidden bg-background-200">
                      <img
                        src={n.cover}
                        alt={n.title}
                        title={`${n.title} - 欲望之岛资讯`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <p className="mt-2 text-sm text-foreground-800 line-clamp-2 group-hover:text-accent-600 transition-colors">
                      {n.title}
                    </p>
                    <span className="text-[11px] text-foreground-500 font-label">{n.date}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}